import type { SceneDocument, SceneElement, Vec2 } from "@calcplan/domain";
import {
  renderedElementPlanGeometry,
  renderedElementVerticalIntervalMm,
} from "./rendered-envelope";

export type PlanClearanceDirection = "left" | "right" | "top" | "bottom";

export interface PlanClearance {
  direction: PlanClearanceDirection;
  fromMm: Vec2;
  toMm: Vec2;
  distanceMm: number;
}

interface ObstacleBox {
  center: Vec2;
  axisX: Vec2;
  axisZ: Vec2;
  halfX: number;
  halfZ: number;
}

const DIRECTIONS: Array<{
  direction: PlanClearanceDirection;
  vector: Vec2;
}> = [
  { direction: "left", vector: [-1, 0] },
  { direction: "right", vector: [1, 0] },
  { direction: "top", vector: [0, -1] },
  { direction: "bottom", vector: [0, 1] },
];

/**
 * Calculates the four live, orthogonal clearances shown around a selected
 * component. Walls and other visible components compete for the nearest hit.
 */
export function computePlanClearances(
  scene: SceneDocument,
  selected: SceneElement,
): PlanClearance[] {
  const selectedBox = elementBox(selected);
  const selectedHalfWidth = axisAlignedHalfExtent(selectedBox, [1, 0]);
  const selectedHalfDepth = axisAlignedHalfExtent(selectedBox, [0, 1]);
  const selectedVertical = verticalInterval(selected);
  const visibleLayers = new Map(scene.layers.map((layer) => [layer.id, layer]));
  const obstacles: ObstacleBox[] = [];

  for (const wall of scene.walls) {
    const layer = visibleLayers.get(wall.layerId);
    if (layer?.visible === false) continue;
    const dx = wall.endMm[0] - wall.startMm[0];
    const dz = wall.endMm[1] - wall.startMm[1];
    const length = Math.hypot(dx, dz);
    if (length < 1e-6) continue;
    const axisX: Vec2 = [dx / length, dz / length];
    obstacles.push({
      center: [
        (wall.startMm[0] + wall.endMm[0]) / 2,
        (wall.startMm[1] + wall.endMm[1]) / 2,
      ],
      axisX,
      axisZ: [-axisX[1], axisX[0]],
      halfX: length / 2,
      halfZ: Math.max(0.5, wall.thicknessMm / 2),
    });
  }

  for (const element of scene.elements) {
    if (element.id === selected.id || !element.visible) continue;
    if (visibleLayers.get(element.layerId)?.visible === false) continue;
    if (!intervalsOverlap(selectedVertical, verticalInterval(element))) continue;
    obstacles.push(elementBox(element));
  }

  return DIRECTIONS.flatMap(({ direction, vector }) => {
    const selectedExtent = vector[0] === 0 ? selectedHalfDepth : selectedHalfWidth;
    const fromMm: Vec2 = [
      selectedBox.center[0] + vector[0] * selectedExtent,
      selectedBox.center[1] + vector[1] * selectedExtent,
    ];
    let nearest = Number.POSITIVE_INFINITY;
    for (const obstacle of obstacles) {
      const hit = rayBoxDistance(fromMm, vector, obstacle);
      if (hit !== null && hit >= -0.5 && hit < nearest) nearest = Math.max(0, hit);
    }
    if (!Number.isFinite(nearest) || nearest < 1) return [];
    return [{
      direction,
      fromMm,
      toMm: [fromMm[0] + vector[0] * nearest, fromMm[1] + vector[1] * nearest],
      distanceMm: nearest,
    } satisfies PlanClearance];
  });
}

function elementBox(element: SceneElement): ObstacleBox {
  const geometry = renderedElementPlanGeometry(element);
  const angle = element.transform.rotationRad[1];
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    center: geometry.centerMm,
    axisX: [cos, sin],
    axisZ: [-sin, cos],
    halfX: geometry.widthMm / 2,
    halfZ: geometry.depthMm / 2,
  };
}

function axisAlignedHalfExtent(box: ObstacleBox, axis: Vec2): number {
  return Math.abs(dot(box.axisX, axis)) * box.halfX
    + Math.abs(dot(box.axisZ, axis)) * box.halfZ;
}

function rayBoxDistance(origin: Vec2, direction: Vec2, box: ObstacleBox): number | null {
  const relative: Vec2 = [origin[0] - box.center[0], origin[1] - box.center[1]];
  const localOrigin: Vec2 = [dot(relative, box.axisX), dot(relative, box.axisZ)];
  const localDirection: Vec2 = [dot(direction, box.axisX), dot(direction, box.axisZ)];
  let minimum = Number.NEGATIVE_INFINITY;
  let maximum = Number.POSITIVE_INFINITY;

  for (const [position, velocity, half] of [
    [localOrigin[0], localDirection[0], box.halfX],
    [localOrigin[1], localDirection[1], box.halfZ],
  ] as const) {
    if (Math.abs(velocity) < 1e-9) {
      if (position < -half || position > half) return null;
      continue;
    }
    const first = (-half - position) / velocity;
    const second = (half - position) / velocity;
    minimum = Math.max(minimum, Math.min(first, second));
    maximum = Math.min(maximum, Math.max(first, second));
    if (minimum > maximum) return null;
  }
  if (maximum < -0.5) return null;
  return minimum >= -0.5 ? minimum : maximum;
}

function verticalInterval(element: SceneElement): [number, number] {
  return renderedElementVerticalIntervalMm(element);
}

function intervalsOverlap(first: [number, number], second: [number, number]): boolean {
  return first[0] <= second[1] && second[0] <= first[1];
}

function dot(first: Vec2, second: Vec2): number {
  return first[0] * second[0] + first[1] * second[1];
}
